import type { FieldType, SchemaField, SyncrelEntity, SyncrelState } from "../domain/types";
import { activeFrameworkFields } from "../domain/framework-fields";
import { frameworkViewState } from "../domain/framework-scope";
import { migrateLegacyFieldReferences } from "../domain/state-migrations";
import { escapeHtml, readField } from "./render-utils";

export type GridSortDirection = "asc" | "desc";

export interface GridSort {
  key: string;
  direction: GridSortDirection;
}

const baseColumns: SchemaField[] = [
  { key: "name", label: "Entity", type: "text" },
  { key: "status", label: "Status", type: "select", options: ["active", "planned", "done", "archived"] },
  { key: "type", label: "Type", type: "select", options: ["goal", "actor", "impact", "feature", "deliverable", "note"] },
];

const fieldTypeOptions: FieldType[] = [
  "text",
  "number",
  "currency",
  "percent",
  "select",
  "multi-select",
  "boolean",
  "computed",
];

const gridFields = (state: SyncrelState): SchemaField[] => (
  [...baseColumns, ...activeFrameworkFields(state)].filter((field, index, all) => (
    all.findIndex((item) => item.key === field.key) === index
  ))
);

export const renderStructuralGrid = (state: SyncrelState, sort: GridSort | null = null): string => {
  const cleanState = migrateLegacyFieldReferences(state);
  const view = frameworkViewState(cleanState);
  const fields = gridFields(view);
  const rows = sortGridEntities(view, sort);
  return `
    <section class="panel panel--grid">
      <div class="panel-head">
        <div>
          <h2>Fields</h2>
          <span>${state.workspace.activeFramework ?? "base"} schema + entity rows</span>
        </div>
        <div class="grid-panel-actions" aria-label="Entity row actions">
          <button type="button" data-add-entity title="Create entity">New entity</button>
          <button type="button" data-delete-checked-entities title="Delete checked rows">Delete checked</button>
        </div>
      </div>
      <div class="schema-workbench">
        ${renderSchemaBuilder(cleanState)}
        <div class="grid-scroll">
          <table class="strategy-grid">
            <thead>
              <tr>
                <th class="grid-check">
                  <label class="grid-check__box" title="Select all rows for exports, Linear, or delete">
                    <input type="checkbox" data-export-check-all aria-label="Select all rows for row actions" />
                  </label>
                </th>
                ${fields.map((field) => renderSortableHeader(field, sort)).join("")}
                <th class="grid-row-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${view.entities.length === 0 ? `
                <tr>
                  <td colspan="${fields.length + 2}">
                    <div class="grid-empty">
                      <strong>No rows yet</strong>
                      <span>Create an entity here or on the canvas; the same item appears everywhere.</span>
                      <button type="button" data-add-entity title="Create entity" aria-label="Create entity">+</button>
                    </div>
                  </td>
                </tr>
              ` : rows.map((entity) => `
                <tr data-row-id="${escapeHtml(entity.id)}" class="${entity.id === cleanState.workspace.selectedEntityId ? "is-selected" : ""}">
                  <td class="grid-check">
                    <label class="grid-check__box" title="Select row for exports, Linear, or delete">
                      <input type="checkbox" data-export-check value="${escapeHtml(entity.id)}" aria-label="Select ${escapeHtml(entity.name)} for row actions" />
                    </label>
                  </td>
                  ${fields.map((field) => {
                    if (field.key === "name") {
                      return `<td><input data-grid-name="${escapeHtml(entity.id)}" value="${escapeHtml(entity.name)}" /></td>`;
                    }
                    if (field.key === "status") {
                      return `<td>${selectCell(entity.id, "status", entity.status, ["active", "planned", "done", "archived"])}</td>`;
                    }
                    if (field.key === "type") {
                      return `<td><span class="type-mark">${escapeHtml(entity.type)}</span></td>`;
                    }
                    if (field.readonly || field.type === "computed") {
                      return `<td class="readonly-cell">${escapeHtml(readField(entity, field.key))}</td>`;
                    }
                    if (field.type === "boolean") {
                      return `<td><label class="grid-boolean-cell"><input type="checkbox" data-field-key="${escapeHtml(field.key)}" data-field-entity="${escapeHtml(entity.id)}" ${readField(entity, field.key) === true ? "checked" : ""} /></label></td>`;
                    }
                    if (field.options) {
                      return `<td>${selectCell(entity.id, field.key, String(readField(entity, field.key)), field.options)}</td>`;
                    }
                    return `<td><input data-field-key="${escapeHtml(field.key)}" data-field-entity="${escapeHtml(entity.id)}" value="${escapeHtml(readField(entity, field.key))}" /></td>`;
                  }).join("")}
                  <td class="grid-row-actions">
                    <button type="button" data-delete-entity="${escapeHtml(entity.id)}" title="Delete ${escapeHtml(entity.name)}" aria-label="Delete ${escapeHtml(entity.name)}">Delete</button>
                  </td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  `;
};

export const sortGridEntities = (state: SyncrelState, sort: GridSort | null = null): SyncrelEntity[] => {
  const view = frameworkViewState(state);
  if (!sort) return view.entities;
  const field = gridFields(view).find((item) => item.key === sort.key);
  if (!field) return view.entities;

  const direction = sort.direction === "desc" ? -1 : 1;
  return [...view.entities].sort((a, b) => {
    const compared = compareGridValues(gridSortValue(a, field), gridSortValue(b, field));
    if (compared !== 0) return compared * direction;
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
      || a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: "base" });
  });
};

const renderSortableHeader = (field: SchemaField, sort: GridSort | null): string => {
  const active = sort?.key === field.key;
  const nextDirection = active && sort?.direction === "asc" ? "desc" : "asc";
  const ariaSort = active
    ? sort?.direction === "desc" ? "descending" : "ascending"
    : "none";
  const indicator = active ? sort?.direction.toUpperCase() : "";

  return `
    <th aria-sort="${ariaSort}">
      <button
        type="button"
        class="grid-sort-button ${active ? "is-active" : ""}"
        data-grid-sort-key="${escapeHtml(field.key)}"
        data-grid-sort-next="${nextDirection}"
        aria-label="Sort by ${escapeHtml(field.label)} ${nextDirection === "asc" ? "ascending" : "descending"}"
      >
        <span>${escapeHtml(field.label)}</span>
        <span class="grid-sort-indicator" aria-hidden="true">${escapeHtml(indicator)}</span>
      </button>
    </th>
  `;
};

const gridSortValue = (entity: SyncrelEntity, field: SchemaField): unknown => {
  if (field.key === "name") return entity.name;
  if (field.key === "status") return entity.status;
  if (field.key === "type") return entity.type;
  return readField(entity, field.key);
};

const compareGridValues = (a: unknown, b: unknown): number => {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);

  const aNumber = typeof a === "string" && a.trim() !== "" ? Number(a) : NaN;
  const bNumber = typeof b === "string" && b.trim() !== "" ? Number(b) : NaN;
  if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return aNumber - bNumber;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

const renderSchemaBuilder = (state: SyncrelState): string => `
  <form class="schema-builder" data-schema-form>
    <div class="schema-builder__head">
      <strong>Schema builder</strong>
      <span>Add fields once; every canvas card and grid row gets the field.</span>
    </div>
    <label>
      <span>Label</span>
      <input name="label" data-schema-label placeholder="Confidence" autocomplete="off" />
    </label>
    <label>
      <span>Type</span>
      <select name="type" data-schema-type>
        ${fieldTypeOptions.map((type) => `<option value="${escapeHtml(type)}">${escapeHtml(type)}</option>`).join("")}
      </select>
    </label>
    <label>
      <span>Options</span>
      <input name="options" data-schema-options placeholder="High, Medium, Low" autocomplete="off" />
    </label>
    <label>
      <span>Default</span>
      <input name="defaultValue" data-schema-default placeholder="Optional" autocomplete="off" />
    </label>
    <label class="schema-check">
      <input type="checkbox" name="required" data-schema-required />
      <span>Required</span>
    </label>
    <label class="schema-check">
      <input type="checkbox" name="readonly" data-schema-readonly />
      <span>Read-only</span>
    </label>
    <button type="submit" data-add-field>Add field</button>
    <div class="schema-field-list">
      ${state.schema.fields.length === 0
        ? `<span>No custom fields yet</span>`
        : state.schema.fields.map((field) => `
          <span title="${escapeHtml(field.description ?? "")}">
            ${escapeHtml(field.label)}
            <small>${escapeHtml(field.type)}${field.required ? " *" : ""}</small>
          </span>
        `).join("")}
    </div>
  </form>
`;

const selectCell = (entityId: string, key: string, value: string, options: string[]): string => `
  <select data-field-key="${escapeHtml(key)}" data-field-entity="${escapeHtml(entityId)}">
    ${options.map((option) => `<option value="${escapeHtml(option)}" ${option === value ? "selected" : ""}>${escapeHtml(option)}</option>`).join("")}
  </select>
`;
